import { useMemo } from "react";
import dayjs from "dayjs";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { SortDirection } from "@/components/tables/types";
import { useAppState } from "@/modules/app/useAppContext";
import type { OpportunitiesFilters, GroupedOpportunitiesFilters } from "../../schemas";

// Read-side status values the regular Opportunities tab is allowed to
// send to `/opportunities/`. "implemented" was deliberately removed from
// the user-selectable filter set (it lives on its own tab now), but
// deep-linked URLs / persisted filter state can still smuggle the value
// through — we explicitly allowlist before forwarding to the API.
const READ_STATUS_ALLOWLIST = new Set(["active", "dismissed"]);

interface UseDiscoverFiltersProps {
  sortAttribute?: string;
  sortOrder?: SortDirection;
}

export function useDiscoverFilters({ sortAttribute, sortOrder }: UseDiscoverFiltersProps = {}) {
  const { getFilterValue, isInitialized } = useDatatableContext();
  const { page, pageSize } = useTablePagination();
  const { isDatabricksTenant } = useAppState();

  // Get date filters from context
  const startDateValue = getFilterValue<string>("startDate");
  const endDateValue = getFilterValue<string>("endDate");

  const startDate = useMemo(() => {
    if (!startDateValue) return dayjs().subtract(30, "day").toDate();
    return new Date(startDateValue);
  }, [startDateValue]);

  const endDate = useMemo(() => {
    if (!endDateValue) return dayjs().toDate();
    return new Date(endDateValue);
  }, [endDateValue]);

  // Discover-specific filter getters
  const effortFilter = useMemo(() => getFilterValue<string[]>("effort") || [], [getFilterValue]);
  const resourceNameFilter = useMemo(
    () => getFilterValue<string[]>("resource_name") || [],
    [getFilterValue]
  );
  const opportunityNameFilter = useMemo(
    () => getFilterValue<string[]>("opportunity_name") || [],
    [getFilterValue]
  );
  const resourceTypeFilter = useMemo(
    () => getFilterValue<string[]>("resource_type") || [],
    [getFilterValue]
  );
  const ownerFilter = useMemo(() => getFilterValue<string[]>("owner") || [], [getFilterValue]);
  const statusFilter = useMemo(
    () => (getFilterValue<string[]>("status") || []).filter(v => READ_STATUS_ALLOWLIST.has(v)),
    [getFilterValue]
  );
  const userFilter = useMemo(() => getFilterValue<string[]>("user") || [], [getFilterValue]);
  const warehouseFilter = useMemo(
    () => getFilterValue<string[]>("warehouse") || [],
    [getFilterValue]
  );
  const instanceFilter = useMemo(
    () => getFilterValue<string[]>("instance") || [],
    [getFilterValue]
  );
  // Databricks workspace filter sourced from the More-Filters Workspace
  // chip — mirrors the SQL Warehouses page pattern (URL key `workspaceId`).
  // Forwarded as `workspace_id` to GetOpportunitiesDataRequest. Defense-
  // in-depth: gate the URL read on ``isDatabricksTenant`` so a tampered /
  // shared / stale URL on a Snowflake tenant never puts the param on the
  // wire (the BE also drops it via ``_build_explicit_workspace_filter``'s
  // tenant gate; this just keeps the wire payload clean).
  const workspaceFilter = useMemo(
    () => (isDatabricksTenant ? getFilterValue<string[]>("workspaceId") || [] : []),
    [getFilterValue, isDatabricksTenant]
  );

  // Convert filters to API parameters for opportunities
  const filterParams = useMemo((): OpportunitiesFilters => {
    const params: OpportunitiesFilters = {
      page: page + 1,
      size: pageSize,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() || "",
    };

    if (sortAttribute && sortOrder) {
      params.sort = sortAttribute;
      params.sort_order = sortOrder;
    }

    if (effortFilter.length > 0) params.effort = effortFilter;
    if (resourceNameFilter.length > 0) params.resource_name = resourceNameFilter;
    if (opportunityNameFilter.length > 0) params.opportunity_name = opportunityNameFilter;
    if (resourceTypeFilter.length > 0) params.resource_type = resourceTypeFilter;
    if (ownerFilter.length > 0) params.owner = ownerFilter;
    if (statusFilter.length > 0) params.status = statusFilter;
    if (userFilter.length > 0) params.user = userFilter;
    if (warehouseFilter.length > 0) params.warehouse = warehouseFilter;
    if (instanceFilter.length > 0) params.instance_id = instanceFilter;
    if (workspaceFilter.length > 0) params.workspace_id = workspaceFilter;

    return params;
  }, [
    page,
    pageSize,
    startDate,
    endDate,
    sortAttribute,
    sortOrder,
    effortFilter,
    resourceNameFilter,
    opportunityNameFilter,
    resourceTypeFilter,
    ownerFilter,
    statusFilter,
    userFilter,
    warehouseFilter,
    instanceFilter,
    workspaceFilter,
  ]);

  // Convert filters to API parameters for grouped opportunities
  const groupedFilterParams = useMemo((): GroupedOpportunitiesFilters => {
    const params: GroupedOpportunitiesFilters = {
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() || "",
    };

    if (effortFilter.length > 0) params.effort = effortFilter;
    if (resourceNameFilter.length > 0) params.resource_name = resourceNameFilter;
    if (opportunityNameFilter.length > 0) params.opportunity_name = opportunityNameFilter;
    if (resourceTypeFilter.length > 0) params.resource_type = resourceTypeFilter;
    if (ownerFilter.length > 0) params.owner = ownerFilter;
    if (statusFilter.length > 0) params.status = statusFilter;
    if (userFilter.length > 0) params.user = userFilter;
    if (warehouseFilter.length > 0) params.warehouse = warehouseFilter;
    if (instanceFilter.length > 0) params.instance_id = instanceFilter;

    return params;
  }, [
    startDate,
    endDate,
    effortFilter,
    resourceNameFilter,
    opportunityNameFilter,
    resourceTypeFilter,
    ownerFilter,
    statusFilter,
    userFilter,
    warehouseFilter,
    instanceFilter,
  ]);

  return {
    filterParams,
    groupedFilterParams,
    isInitialized,
  };
}
